/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { doc, getDoc, updateDoc, collection, addDoc, onSnapshot } from "firebase/firestore"
import { httpsCallable } from "firebase/functions"
import { QRCodeSVG } from "qrcode.react"
import { db, functions } from "../../firebase"
import env from "../../env"
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material"
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DescriptionIcon from '@mui/icons-material/Description'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import PaymentIcon from '@mui/icons-material/Payment'
import LinkIcon from '@mui/icons-material/Link'
import { toast } from 'react-toastify'
import useCollectionSubscription from "../../components/hooks/useCollectionSubscription"
import { DeliveryRecipient } from "../../models/deliveryRecipient"
import { PREFECTURES } from "./lib/prefectures"
import { DELIVERY_CARRIERS } from "./lib/deliveryCarriers"
import { DELIVERY_TIME_ZONES } from "./lib/deliveryTimeZones"
import { detectMobileOS, buildSquareDeepLink } from "./lib/square"

interface IPartPrice {
  id: string
  part: string
  priceUnit: 'perPiece' | 'perGram'
  frozenPrice: number
  chilledPrice: number
  description: string
}

const ShipmentDetails = () => {
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [shipment, setShipment] = useState<Record<string, any> | null>(null)
  const [notFound, setNotFound] = useState<boolean>(false)
  const [isEditingFee, setIsEditingFee] = useState<boolean>(false)
  const [shippingFeeInput, setShippingFeeInput] = useState<string>('0')
  const [savingFee, setSavingFee] = useState<boolean>(false)
  const [isEditingTrackingNumber, setIsEditingTrackingNumber] = useState<boolean>(false)
  const [trackingNumberInput, setTrackingNumberInput] = useState<string>('')
  const [deliveryCarrierInput, setDeliveryCarrierInput] = useState<string>('')
  const [savingTrackingNumber, setSavingTrackingNumber] = useState<boolean>(false)
  const [isEditingDiscount, setIsEditingDiscount] = useState<boolean>(false)
  const [discountInput, setDiscountInput] = useState<string>('0')
  const [savingDiscount, setSavingDiscount] = useState<boolean>(false)
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null)
  const [storageTypeInput, setStorageTypeInput] = useState<string>('冷凍')
  const [savingStorageType, setSavingStorageType] = useState<boolean>(false)
  const [partPrices, setPartPrices] = useState<IPartPrice[]>([])

  // 送付先情報（佐川急便 送り状用）
  const recipients = useCollectionSubscription(collection(db, 'deliveryRecipients'), DeliveryRecipient)
  const [selectedRecipientId, setSelectedRecipientId] = useState<string>('')
  const [recipientName, setRecipientName] = useState<string>('')
  const [recipientPostalCode, setRecipientPostalCode] = useState<string>('')
  const [recipientPrefecture, setRecipientPrefecture] = useState<string>('')
  const [recipientAddress, setRecipientAddress] = useState<string>('')
  const [recipientPhone, setRecipientPhone] = useState<string>('')
  const [deliveryTimeZone, setDeliveryTimeZone] = useState<string>('指定なし')
  const [savingRecipient, setSavingRecipient] = useState<boolean>(false)
  const [savingRecipientMaster, setSavingRecipientMaster] = useState<boolean>(false)

  // freee 現金売上取引登録
  const registerCashSaleDeal = httpsCallable<{ shipmentId: string }, { dealId: number }>(
    functions, 'registerFreeecashSaleDeal'
  )
  const [freeeDealId, setFreeeDealId] = useState<number | null>(null)
  const [registeringDeal, setRegisteringDeal] = useState<boolean>(false)

  // Square 決済リンク発行
  const createSquarePaymentLink = httpsCallable<{ shipmentId: string }, { url: string; id: string }>(
    functions, 'createSquarePaymentLink'
  )
  const [creatingPaymentLink, setCreatingPaymentLink] = useState<boolean>(false)

  // 品名情報（佐川急便 送り状用）
  const [itemName2, setItemName2] = useState<string>('')
  const [itemName3, setItemName3] = useState<string>('')
  const [itemName4, setItemName4] = useState<string>('')
  const [savingItemNames, setSavingItemNames] = useState<boolean>(false)

  useEffect(() => {
    if (!shipmentId) return
    getDoc(doc(db, 'shipments', shipmentId)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data()
        setShipment({ id: snap.id, ...data })
        setShippingFeeInput(String(Number(data.shippingFee) || 0))
        setDiscountInput(String(Number(data.discount) || 0))
        setTrackingNumberInput(data.trackingNumber || '')
        setDeliveryCarrierInput(data.deliveryCarrier || '')
        const recipient = data.deliveryRecipient
        if (recipient) {
          setRecipientName(recipient.name || '')
          setRecipientPostalCode(recipient.postalCode || '')
          setRecipientPrefecture(recipient.prefecture || '')
          setRecipientAddress(recipient.address || '')
          setRecipientPhone(recipient.phone || '')
        }
        if (typeof data.itemName2 === 'string') setItemName2(data.itemName2)
        if (typeof data.itemName3 === 'string') setItemName3(data.itemName3)
        if (typeof data.itemName4 === 'string') setItemName4(data.itemName4)
        if (typeof data.deliveryTimeZone === 'string') setDeliveryTimeZone(data.deliveryTimeZone)
        if (typeof data.freeeDealId === 'number') setFreeeDealId(data.freeeDealId)
      } else {
        setNotFound(true)
      }
    })
  }, [shipmentId])

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'partPrices'), (snapshot) => {
      const prices = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as IPartPrice))
      setPartPrices(prices)
    })
    return unsubscribe
  }, [])

  const calculatePrice = (item: any, storageType: string): number => {
    const partPrice = partPrices.find((p) => p.part === item.part)
    if (!partPrice) return Number(item.price) || 0

    const weightInGrams = typeof item.weight === 'string'
      ? parseFloat(item.weight.replace(/[^\d.-]/g, '')) || 0
      : (typeof item.weight === 'number' ? item.weight : 0)
    const weightInKg = weightInGrams / 1000
    const quantity = Number(item.quantity) || 1
    const pricePerUnit = (storageType === '冷凍' || !storageType) ? partPrice.frozenPrice : partPrice.chilledPrice

    if (partPrice.priceUnit === 'perPiece') {
      return Math.round(pricePerUnit * quantity)
    } else {
      return Math.round(pricePerUnit * weightInKg)
    }
  }

  const handleSelectRecipient = (recipient: DeliveryRecipient | null) => {
    setSelectedRecipientId(recipient?.id || '')
    if (!recipient) return
    setRecipientName(recipient.name)
    setRecipientPostalCode(recipient.postalCode)
    setRecipientPrefecture(recipient.prefecture)
    setRecipientAddress(recipient.address)
    setRecipientPhone(recipient.phone)
  }

  const saveDeliveryRecipient = async () => {
    if (!shipmentId) return
    const deliveryRecipient = {
      name: recipientName,
      postalCode: recipientPostalCode,
      prefecture: recipientPrefecture,
      address: recipientAddress,
      phone: recipientPhone,
    }
    setSavingRecipient(true)
    try {
      await updateDoc(doc(db, 'shipments', shipmentId), { deliveryRecipient, deliveryTimeZone })
      setShipment((prev) => prev ? { ...prev, deliveryRecipient, deliveryTimeZone } : prev)
      toast.success("送付先情報を更新しました")
    } catch (e) {
      toast.error("送付先情報の更新に失敗しました: " + JSON.stringify(e))
    } finally {
      setSavingRecipient(false)
    }
  }

  const saveDeliveryRecipientToMaster = async () => {
    if (!recipientName) {
      toast.warn("送付先氏名を入力してください")
      return
    }
    setSavingRecipientMaster(true)
    try {
      const data = {
        name: recipientName,
        postalCode: recipientPostalCode,
        prefecture: recipientPrefecture,
        address: recipientAddress,
        phone: recipientPhone,
        customerName: shipment?.customer || '',
        updatedAt: new Date(),
      }
      if (selectedRecipientId) {
        await updateDoc(doc(db, 'deliveryRecipients', selectedRecipientId), data)
      } else {
        const docRef = await addDoc(collection(db, 'deliveryRecipients'), data)
        setSelectedRecipientId(docRef.id)
      }
      toast.success("送付先マスタを更新しました")
    } catch (e) {
      toast.error("送付先マスタの更新に失敗しました: " + JSON.stringify(e))
    } finally {
      setSavingRecipientMaster(false)
    }
  }

  const saveItemNames = async () => {
    if (!shipmentId) return
    setSavingItemNames(true)
    try {
      await updateDoc(doc(db, 'shipments', shipmentId), { itemName2, itemName3, itemName4 })
      setShipment((prev) => prev ? { ...prev, itemName2, itemName3, itemName4 } : prev)
      toast.success("品名情報を更新しました")
    } catch (e) {
      toast.error("品名情報の更新に失敗しました: " + JSON.stringify(e))
    } finally {
      setSavingItemNames(false)
    }
  }

  const saveShippingFee = async () => {
    if (!shipmentId) return
    const fee = Math.max(0, parseInt(shippingFeeInput) || 0)
    setSavingFee(true)
    try {
      await updateDoc(doc(db, 'shipments', shipmentId), { shippingFee: fee })
      setShipment((prev) => prev ? { ...prev, shippingFee: fee } : prev)
      setShippingFeeInput(String(fee))
      setIsEditingFee(false)
      toast.success("送料を更新しました")
    } catch (e) {
      toast.error("送料の更新に失敗しました: " + JSON.stringify(e))
    } finally {
      setSavingFee(false)
    }
  }

  const cancelEditShippingFee = () => {
    setShippingFeeInput(String(Number(shipment?.shippingFee) || 0))
    setIsEditingFee(false)
  }

  const saveTrackingNumber = async () => {
    if (!shipmentId) return
    const trackingNumber = trackingNumberInput.trim()
    const deliveryCarrier = deliveryCarrierInput.trim()
    setSavingTrackingNumber(true)
    try {
      await updateDoc(doc(db, 'shipments', shipmentId), { trackingNumber, deliveryCarrier })
      setShipment((prev) => prev ? { ...prev, trackingNumber, deliveryCarrier } : prev)
      setTrackingNumberInput(trackingNumber)
      setDeliveryCarrierInput(deliveryCarrier)
      setIsEditingTrackingNumber(false)
      toast.success("宅配便番号を更新しました")
    } catch (e) {
      toast.error("宅配便番号の更新に失敗しました: " + JSON.stringify(e))
    } finally {
      setSavingTrackingNumber(false)
    }
  }

  const cancelEditTrackingNumber = () => {
    setTrackingNumberInput(shipment?.trackingNumber || '')
    setDeliveryCarrierInput(shipment?.deliveryCarrier || '')
    setIsEditingTrackingNumber(false)
  }

  const saveDiscount = async () => {
    if (!shipmentId) return
    const discount = Math.max(0, parseInt(discountInput) || 0)
    setSavingDiscount(true)
    try {
      await updateDoc(doc(db, 'shipments', shipmentId), { discount })
      setShipment((prev) => prev ? { ...prev, discount } : prev)
      setDiscountInput(String(discount))
      setIsEditingDiscount(false)
      toast.success("値引きを更新しました")
    } catch (e) {
      toast.error("値引きの更新に失敗しました: " + JSON.stringify(e))
    } finally {
      setSavingDiscount(false)
    }
  }

  const cancelEditDiscount = () => {
    setDiscountInput(String(Number(shipment?.discount) || 0))
    setIsEditingDiscount(false)
  }

  const startEditStorageType = (index: number, currentValue?: string) => {
    setEditingProductIndex(index)
    setStorageTypeInput(currentValue === 'チルド' ? 'チルド' : '冷凍')
  }

  const cancelEditStorageType = () => {
    setEditingProductIndex(null)
  }

  const saveStorageType = async (index: number) => {
    if (!shipmentId || !shipment) return
    const currentProducts: any[] = Array.isArray(shipment.products) ? shipment.products : []
    const updatedProducts = currentProducts.map((p, i) => i === index
      ? { ...p, storageType: storageTypeInput, price: calculatePrice(p, storageTypeInput) }
      : p)
    const totalPrice = updatedProducts.reduce((sum, p) => sum + (Number(p.price) || 0), 0)
    setSavingStorageType(true)
    try {
      await updateDoc(doc(db, 'shipments', shipmentId), { products: updatedProducts, totalPrice })
      setShipment((prev) => prev ? { ...prev, products: updatedProducts, totalPrice } : prev)
      setEditingProductIndex(null)
      toast.success("温度状態と価格を更新しました")
    } catch (e) {
      toast.error("温度状態の更新に失敗しました: " + JSON.stringify(e))
    } finally {
      setSavingStorageType(false)
    }
  }

  const squareApplicationId = env("SQUARE_APPLICATION_ID")
  const mobileOS = detectMobileOS()

  const handleSquarePayment = (amount: number) => {
    if (!shipmentId || !squareApplicationId) return
    // Squareダッシュボードの「Point of Sale API」→「Web Callback URL」には固定URLしか登録できないため、
    // 出荷IDはURLパスではなくstateパラメータで渡し、固定の /admin/shipments/square-callback で受け取る
    const callbackUrl = `${window.location.origin}/admin/shipments/square-callback`
    const deepLink = buildSquareDeepLink({
      os: mobileOS,
      amount,
      clientId: squareApplicationId,
      callbackUrl,
      note: `出荷ID: ${shipmentId}`,
      state: shipmentId,
    })
    if (!deepLink) return
    window.location.href = deepLink
  }

  const handleCreateSquarePaymentLink = async () => {
    if (!shipmentId) return
    setCreatingPaymentLink(true)
    try {
      const result = await createSquarePaymentLink({ shipmentId })
      setShipment((prev) => prev ? {
        ...prev,
        squarePaymentLinkUrl: result.data.url,
        squarePaymentLinkId: result.data.id,
      } : prev)
      toast.success('決済リンクを発行しました')
    } catch (e: any) {
      toast.error('決済リンクの発行に失敗しました: ' + (e?.message || JSON.stringify(e)))
    } finally {
      setCreatingPaymentLink(false)
    }
  }

  const handleCopyPaymentLink = async () => {
    const url = shipment?.squarePaymentLinkUrl
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      toast.success('決済リンクをコピーしました')
    } catch {
      toast.error('コピーに失敗しました')
    }
  }

  const handleMarkPaymentLinkPaid = async () => {
    if (!shipmentId) return
    if (!window.confirm('決済リンクの支払いが完了したことを確認しましたか？')) return
    try {
      await updateDoc(doc(db, 'shipments', shipmentId), {
        squarePaymentStatus: 'completed',
        squarePaidAt: new Date().toISOString(),
      })
      setShipment((prev) => prev ? { ...prev, squarePaymentStatus: 'completed' } : prev)
      toast.success('支払い済みにしました')
    } catch (e) {
      toast.error('更新に失敗しました: ' + JSON.stringify(e))
    }
  }

  const handleRegisterCashSaleDeal = async () => {
    if (!shipmentId) return
    if (!window.confirm('freeeに現金売上として取引登録します。よろしいですか？')) return
    setRegisteringDeal(true)
    try {
      const result = await registerCashSaleDeal({ shipmentId })
      setFreeeDealId(result.data.dealId)
      setShipment((prev) => prev ? { ...prev, freeeDealId: result.data.dealId } : prev)
      toast.success(`freeeに取引登録しました（取引ID: ${result.data.dealId}）`)
    } catch (e: any) {
      toast.error('取引登録に失敗しました: ' + (e?.message || JSON.stringify(e)))
    } finally {
      setRegisteringDeal(false)
    }
  }

  if (notFound) {
    return (
      <Box m={4}>
        <Typography variant="h5">該当する出荷情報が見つかりません</Typography>
        <Box mt={2}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/shipments')}>
            出荷一覧に戻る
          </Button>
        </Box>
      </Box>
    )
  }

  if (!shipment) {
    return <Box m={4}><Typography>読み込み中...</Typography></Box>
  }

  const products: any[] = Array.isArray(shipment.products) ? shipment.products : []
  const totalCount = shipment.totalCount ?? products.length
  const totalWeight = Number(shipment.totalWeight) || 0
  const totalPrice = Number(shipment.totalPrice) || 0
  const shippingFee = Number(shipment.shippingFee) || 0
  const discount = Number(shipment.discount) || 0
  const billingAmount = totalPrice + shippingFee - discount
  const shipmentPageUrl = `${window.location.origin}/admin/shipments/${shipmentId}`

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/shipments')}>
          出荷一覧に戻る
        </Button>
        <Typography variant={isMobile ? "h5" : "h4"}>出荷情報詳細</Typography>
        <Box flexGrow={1} />
        <Button variant="contained" color="info" startIcon={<DescriptionIcon />} onClick={() => navigate(`/admin/shipments/${shipmentId}/document/quotation`)}>
          見積書
        </Button>
        <Button variant="contained" color="success" startIcon={<ReceiptLongIcon />} onClick={() => navigate(`/admin/shipments/${shipmentId}/document/delivery`)}>
          納品書
        </Button>
        {freeeDealId ? (
          <Typography variant="body2" color="text.secondary" alignSelf="center">
            freee登録済み（取引ID: {freeeDealId}）
          </Typography>
        ) : (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ReceiptLongIcon />}
            onClick={handleRegisterCashSaleDeal}
            disabled={registeringDeal}
          >
            {registeringDeal ? '登録中...' : 'freee 現金売上登録'}
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          {squareApplicationId && (
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="body1" fontWeight="bold">Square決済（その場でタップ）:</Typography>
              {shipment.squarePaymentStatus === 'completed' ? (
                <Typography variant="body2" color="success.main">
                  決済済み{shipment.squareTransactionId ? `（取引ID: ${shipment.squareTransactionId}）` : ''}
                </Typography>
              ) : mobileOS !== 'other' ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PaymentIcon />}
                  onClick={() => handleSquarePayment(billingAmount)}
                  disabled={billingAmount <= 0}
                >
                  Squareで決済（¥{billingAmount.toLocaleString('ja-JP')}）
                </Button>
              ) : (
                <Box display="flex" alignItems="center" gap={2}>
                  <QRCodeSVG value={shipmentPageUrl} size={96} />
                  <Typography variant="body2" color="text.secondary">
                    スマートフォン（決済端末）でこのQRコードを読み取り、この画面から「Squareで決済」を実行してください
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="body1" fontWeight="bold">Square決済リンク（顧客に送付）:</Typography>
            {shipment.squarePaymentLinkUrl ? (
              <>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {shipment.squarePaymentLinkUrl}
                </Typography>
                <Button size="small" variant="outlined" onClick={handleCopyPaymentLink}>
                  URLをコピー
                </Button>
                {shipment.squarePaymentStatus === 'completed' ? (
                  <Typography variant="body2" color="success.main">支払い済み</Typography>
                ) : (
                  <Button size="small" variant="outlined" color="success" onClick={handleMarkPaymentLinkPaid}>
                    支払い済みにする
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={handleCreateSquarePaymentLink}
                disabled={creatingPaymentLink || billingAmount <= 0}
              >
                {creatingPaymentLink ? '発行中...' : '決済リンクを発行'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Typography variant="body1"><strong>出荷日:</strong> {shipment.shippingDate}</Typography>
            <Typography variant="body1"><strong>出荷先:</strong> {shipment.customer}</Typography>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Typography variant="body1" component="span"><strong>宅配業者・宅配便番号:</strong></Typography>
            {isEditingTrackingNumber ? (
              <>
                <Autocomplete
                  freeSolo
                  options={DELIVERY_CARRIERS}
                  value={deliveryCarrierInput}
                  onChange={(_, value) => setDeliveryCarrierInput(value || '')}
                  onInputChange={(_, value) => setDeliveryCarrierInput(value)}
                  renderInput={(params) => <TextField {...params} label="宅配業者" size="small" />}
                  sx={{ width: 200 }}
                />
                <TextField
                  size="small"
                  value={trackingNumberInput}
                  onChange={(e) => setTrackingNumberInput(e.target.value)}
                  placeholder="宅配便番号を入力"
                  sx={{ width: 200 }}
                />
                <Button size="small" variant="contained" onClick={saveTrackingNumber} disabled={savingTrackingNumber}>保存</Button>
                <Button size="small" variant="outlined" onClick={cancelEditTrackingNumber} disabled={savingTrackingNumber}>キャンセル</Button>
              </>
            ) : (
              <>
                <Typography variant="body1">
                  {shipment.deliveryCarrier || '-'} / {shipment.trackingNumber || '-'}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => setIsEditingTrackingNumber(true)}>編集</Button>
              </>
            )}
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Typography variant="body1" component="span"><strong>送料（税込）:</strong></Typography>
            {isEditingFee ? (
              <>
                <TextField
                  type="number"
                  size="small"
                  value={shippingFeeInput}
                  onChange={(e) => setShippingFeeInput(e.target.value)}
                  inputProps={{ min: 0, step: 100 }}
                  sx={{ width: 140 }}
                />
                <Button size="small" variant="contained" onClick={saveShippingFee} disabled={savingFee}>保存</Button>
                <Button size="small" variant="outlined" onClick={cancelEditShippingFee} disabled={savingFee}>キャンセル</Button>
              </>
            ) : (
              <>
                <Typography variant="body1">¥{(Number(shipment.shippingFee) || 0).toLocaleString('ja-JP')}</Typography>
                <Button size="small" variant="outlined" onClick={() => setIsEditingFee(true)}>編集</Button>
              </>
            )}
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Typography variant="body1" component="span"><strong>値引き:</strong></Typography>
            {isEditingDiscount ? (
              <>
                <TextField
                  type="number"
                  size="small"
                  value={discountInput}
                  onChange={(e) => setDiscountInput(e.target.value)}
                  inputProps={{ min: 0, step: 100 }}
                  sx={{ width: 140 }}
                />
                <Button size="small" variant="contained" onClick={saveDiscount} disabled={savingDiscount}>保存</Button>
                <Button size="small" variant="outlined" onClick={cancelEditDiscount} disabled={savingDiscount}>キャンセル</Button>
              </>
            ) : (
              <>
                <Typography variant="body1">-¥{(Number(shipment.discount) || 0).toLocaleString('ja-JP')}</Typography>
                <Button size="small" variant="outlined" onClick={() => setIsEditingDiscount(true)}>編集</Button>
              </>
            )}
          </Box>
          <Typography variant="body1"><strong>備考:</strong> {shipment.shippingMemo || '-'}</Typography>
          <Typography variant="body2" color="text.secondary">登録日時: {shipment.createdAt || '-'}</Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={3} flexWrap="wrap">
          <Typography variant="h6" color="primary">件数: {totalCount}件</Typography>
          <Typography variant="h6" color="secondary">総重量: {(totalWeight / 1000).toFixed(1)}kg</Typography>
          <Typography variant="h6" color="success.main">総額（税込）: ¥{totalPrice.toLocaleString('ja-JP')}</Typography>
          <Typography variant="h6" color="warning.main">請求金額（送料・値引き後）: ¥{billingAmount.toLocaleString('ja-JP')}</Typography>
        </Box>
      </Paper>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">送付先情報（送り状用）</Typography>
        </AccordionSummary>
        <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            options={recipients}
            value={recipients.find((r) => r.id === selectedRecipientId) || null}
            onChange={(_, value) => handleSelectRecipient(value)}
            getOptionLabel={(option) => `${option.name}${option.customerName ? `（${option.customerName}）` : ''}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="送付先マスタから選択" size="small" />}
            sx={{ maxWidth: 400 }}
          />
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="送付先氏名"
              size="small"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              sx={{ width: 240 }}
            />
            <TextField
              label="郵便番号"
              size="small"
              placeholder="000-0000"
              value={recipientPostalCode}
              onChange={(e) => setRecipientPostalCode(e.target.value)}
              sx={{ width: 160 }}
            />
            <FormControl size="small" sx={{ width: 160 }}>
              <InputLabel id="recipient-prefecture-label">都道府県</InputLabel>
              <Select
                labelId="recipient-prefecture-label"
                label="都道府県"
                value={recipientPrefecture}
                onChange={(e) => setRecipientPrefecture(e.target.value)}
              >
                <MenuItem value=""><em>未選択</em></MenuItem>
                {PREFECTURES.map((pref) => (
                  <MenuItem key={pref} value={pref}>{pref}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="住所（都道府県を除く）"
              size="small"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              sx={{ width: 320 }}
            />
            <TextField
              label="電話番号"
              size="small"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value)}
              sx={{ width: 180 }}
            />
          </Box>
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel id="delivery-time-zone-label">配達時間帯</InputLabel>
            <Select
              labelId="delivery-time-zone-label"
              label="配達時間帯"
              value={deliveryTimeZone}
              onChange={(e) => setDeliveryTimeZone(e.target.value)}
            >
              {DELIVERY_TIME_ZONES.map((z) => (
                <MenuItem key={z.label} value={z.label}>{z.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" gap={1}>
            <Button variant="contained" onClick={saveDeliveryRecipient} disabled={savingRecipient}>
              出荷情報に保存
            </Button>
            <Button variant="outlined" onClick={saveDeliveryRecipientToMaster} disabled={savingRecipientMaster}>
              送付先マスタに{selectedRecipientId ? '更新' : '登録'}
            </Button>
          </Box>
        </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">品名情報（送り状用）</Typography>
        </AccordionSummary>
        <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField label="品名１" size="small" value="鹿肉" disabled sx={{ width: 200 }} />
            <TextField
              label="品名２"
              size="small"
              value={itemName2}
              onChange={(e) => setItemName2(e.target.value)}
              sx={{ width: 200 }}
            />
            <TextField
              label="品名３"
              size="small"
              value={itemName3}
              onChange={(e) => setItemName3(e.target.value)}
              sx={{ width: 200 }}
            />
            <TextField
              label="品名４"
              size="small"
              value={itemName4}
              onChange={(e) => setItemName4(e.target.value)}
              sx={{ width: 200 }}
            />
          </Box>
          <Box>
            <Button variant="contained" onClick={saveItemNames} disabled={savingItemNames}>
              保存
            </Button>
          </Box>
        </Box>
        </AccordionDetails>
      </Accordion>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>商品番号</TableCell>
              <TableCell>個体識別番号</TableCell>
              <TableCell>部位</TableCell>
              <TableCell>重量(g)</TableCell>
              <TableCell>温度状態</TableCell>
              <TableCell>個数</TableCell>
              <TableCell>価格（税込）</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((p, i) => (
              <TableRow key={p.id || i}>
                <TableCell>{p.productNo || '-'}</TableCell>
                <TableCell>{p.individualNo || '-'}</TableCell>
                <TableCell>{p.part || '-'}</TableCell>
                <TableCell>{p.weight ?? '-'}</TableCell>
                <TableCell>
                  {editingProductIndex === i ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Select
                        size="small"
                        value={storageTypeInput}
                        onChange={(e) => setStorageTypeInput(e.target.value as string)}
                        sx={{ minWidth: 90 }}
                      >
                        <MenuItem value="冷凍">冷凍</MenuItem>
                        <MenuItem value="チルド">チルド</MenuItem>
                      </Select>
                      <Button size="small" variant="contained" onClick={() => saveStorageType(i)} disabled={savingStorageType}>保存</Button>
                      <Button size="small" variant="outlined" onClick={cancelEditStorageType} disabled={savingStorageType}>キャンセル</Button>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{p.storageType || '-'}</Typography>
                      <Button size="small" variant="outlined" onClick={() => startEditStorageType(i, p.storageType)}>編集</Button>
                    </Box>
                  )}
                </TableCell>
                <TableCell>{p.quantity ?? 1}</TableCell>
                <TableCell>{p.price != null ? `¥${Number(p.price).toLocaleString('ja-JP')}` : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default ShipmentDetails
